<template>
	<div
		:class="['badge', ...variantClassList, ...stateClassList]"
		v-if="visible"
		ref="badge"
	>
		<span v-if="variant !== 'rectangle'" class="badge__pill">
			{{ text }}
		</span>
		<span
			v-if="iconVisible"
			class="badge__rectangle-pill"
			ref="rectanglePill"
			tabindex="0"
			@mouseover="onMouseEnter"
			@mouseout="onMouseLeave"
			@focus="onFocus"
			@blur="onBlur"
		>
			<i class="icon icon-warning" />
		</span>
		<span v-if="rectangleVisible" class="badge__rectangle">
			<slot>
				<span class="badge__rectangle-text">
					{{ text }}
				</span>
			</slot>
		</span>

		<Tooltip
			placement="top"
			:visible="displayTooltip || pillOnFocus"
			:trackedRefName="'rectanglePill'"
			positionStrategy="fixed"
			:offset="[0, 8]"
			:state="state"
		>
			{{ this.slotContent }}
		</Tooltip>
	</div>
</template>

<script>
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/badge-variant-classes';
import componentStateMixin from '@/mixins/component-state-mixin';
import Tooltip from '@/components/shells/tooltip/tooltip.vue';

export default {
	name: 'Badge',
	components: {
		Tooltip
	},
	data() {
		return {
			visibility: false,
			displayTooltip: false,
			pillOnFocus: false
		};
	},
	mixins: [componentVariantMixin, componentStateMixin],
	created() {
		this.variantClasses = variantClasses;
	},
	props: {
		text: {
			type: String,
			default: null
		},
		visible: {
			type: Boolean,
			default: false
		}
	},
	methods: {
		onBlur() {
			this.pillOnFocus = false;
			this.displayTooltip = false;
		},
		onMouseEnter() {
			this.displayTooltip = true;
		},
		onMouseLeave() {
			if (this.pillOnFocus) {
				this.displayTooltip = true;
			} else {
				this.displayTooltip = false;
			}
		},
		onFocus() {
			this.pillOnFocus = true;
		}
	},
	computed: {
		rectangleVisible() {
			return this.variant === 'rectangle';
		},
		iconVisible() {
			return this.variant === 'rectangle' && this.isDynamic;
		},
		slotContent() {
			let result = this.text;

			if (
				this.$slots &&
				this.$slots.default &&
				this.$slots.default.length > 0
			) {
				result = this.$slots.default[0].text;
			}

			return result;
		},
		isDynamic() {
			let result = false;
			let index;

			if (this.$attrs) {
				const keys = Object.keys(this.$attrs);

				if (keys) {
					index = keys.findIndex(a => a.includes('minimized'));
				}

				if (index >= 0) {
					result = true;
				}
			}

			return result;
		}
	}
};
</script>

<style lang="scss" scoped>
/*------------------------------------*\
  #BASE-COMPONENT-STYLES
  The base class and variant classes, if applicable, are never added/removed via dev logic.
\*------------------------------------*/
.badge {
	display: inline-block;
	min-width: 22px;
	color: $ui-fg-dark;
	font-size: $txt-supporting-font-size;
	line-height: 16px;

	&__pill {
		display: inline-flex;
		justify-content: center;
		align-items: center;
		height: 22px;
		min-width: 22px;
		padding-left: 6px;
		padding-right: 6px;
		border-radius: 12px;
		background: var(--ui-neutral);
		font-weight: $bold-font-weight;
		border: 2px solid $ui-accessible-need;

		.icon {
			display: inline-block;
			font-size: 16px;
		}
	}

	&__rectangle {
		height: auto;
		background: var(--ui-neutral-light);
		border-radius: $border-radius;
		border: 2px solid var(--ui-neutral);
		padding: $padding-xs $padding-sm;
	}

	&__rectangle-pill {
		display: none;
		font-size: 18px;

		&:focus {
			outline: $focus-width $focus-style var(--brand-2);
			outline-offset: 2px;
		}
	}
}

/*------------------------------------*\
  #STATE-TYPE-STYLES
  This section is only for badge CSS change via state type.
  Neutral state is the default, CSS for that is located in the base component styles section.
  These classes are added/removed as needed via dev logic
\*------------------------------------*/
.badge {
	.badge__rectangle {
		background: var(--ui-neutral-light);
		border-color: var(--ui-neutral);
	}

	.badge__rectangle-pill {
		color: var(--ui-neutral);
	}

	&.is-positive {
		.badge__pill {
			background: var(--ui-positive);
		}

		.badge__rectangle {
			background: var(--ui-positive-light);
			border-color: var(--ui-positive);
			color: $ui-fg-dark;
		}

		.badge__rectangle-pill {
			color: var(--ui-positive);
		}
	}

	&.is-warning {
		.badge__pill {
			background: var(--ui-warning);
		}

		.badge__rectangle {
			background: var(--ui-warning-light);
			border-color: var(--ui-warning);
		}

		.badge__rectangle-pill {
			color: var(--ui-warning);
		}
	}

	&.is-negative {
		.badge__pill {
			background: var(--ui-negative);
			color: $ui-fg-light;
		}

		.badge__rectangle {
			background: var(--ui-negative-light);
			border-color: var(--ui-negative);
			color: $ui-fg-dark;
		}

		.badge__rectangle-pill {
			color: var(--ui-negative);
		}
	}
}

/*------------------------------------*\
#VARIANTS PER BREAKPOINT
/*------------------------------------*/
@include variant-per-breakpoint;
</style>
